// Mock data for The Productive Offenders of Society Foundation

export const siteConfig = {
  name: "Productive Offenders of Society Foundation",
  shortName: "POSF",
  tagline: "Empowering Second Chances, Building Brighter Futures",
  established: "2023",
  email: "info.poosfoundation@gmail.com",
  phone: "+1 (570) 579-7695",
  address: "855 3rd Street, Whitehall, PA 18052",
  website: "https://www.theproductiveoffendersofsocietyfoundation.org",
  socialLinks: {
    facebook: "https://facebook.com/productiveoffenders",
    instagram: "https://www.instagram.com/productiveoffendersofsociety?igsh=MWdzNXNoOTVuZDVqZw%3D%3D&utm_source=qr",
    linkedin: "https://linkedin.com/company/productiveoffenders",
    twitter: "https://twitter.com/prodoffenders"
  },
  donationMethods: {
    check: "Payable to The Productive Offenders of Society Foundation",
    bank: "People's Security Bank (details provided upon request)",
    zelle: "Available upon request",
    paypal: "Available upon request",
    online: "Coming Soon",
    note: "Cash donations are not accepted for accountability and compliance purposes."
  }
};

export const heroContent = {
  headline: "Second Chances Should Come With Real Support",
  subheadline: "We help individuals impacted by the criminal legal system rebuild their lives through housing stability, employment support, recovery resources, and advocacy—guided by lived experience and community partnership.",
  ctaButtons: [
    { label: "Get Help Now", href: "/get-involved", variant: "primary" },
    { label: "Support the Mission", href: "/get-involved#donate", variant: "secondary" },
    { label: "Learn More", href: "/about", variant: "outline" }
  ]
};

export const impactStats = [
  { number: "500+", label: "Lives Transformed", icon: "Users" },
  { number: "85%", label: "Employment Rate", icon: "Briefcase" },
  { number: "50+", label: "Partner Organizations", icon: "Building" },
  { number: "1,200+", label: "Workshop Hours", icon: "Clock" }
];

export const missionContent = {
  title: "Our Mission",
  description: "To break the cycle of recidivism by providing comprehensive support, education, and employment opportunities to formerly incarcerated individuals, helping them rebuild their lives and contribute positively to their communities.",
  values: [
    { title: "Empowerment", description: "We believe in the potential of every individual to transform their lives." },
    { title: "Education", description: "Knowledge and skills are the foundation of lasting change." },
    { title: "Community", description: "Together, we create stronger, safer communities for everyone." }
  ]
};

export const ourWhyContent = {
  title: "Our Why",
  subtitle: "This is where trust is built",
  intro: "People most impacted by the criminal legal system are often excluded from decisions that shape their lives.",
  problem: "Policies, programs, and resources exist—but too often they are created without the voices of those directly affected. This leads to gaps in housing, employment, recovery support, and long-term stability.",
  solution: "We exist to change that.",
  belief: "At The Productive Offenders of Society Foundation, we believe:",
  coreMessage: "Those closest to the problem are closest to the solution.",
  approach: "We center lived experience, community voice, and collaboration to create real pathways forward—not temporary fixes."
};

export const testimonials = [
  {
    id: 1,
    name: "Marcus Johnson",
    role: "Program Graduate, 2024",
    quote: "This foundation gave me more than a second chance—they gave me the tools and confidence to build a career I'm proud of. I now mentor others on the same journey.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
  },
  {
    id: 2,
    name: "Sarah Williams",
    role: "Partner Company HR Director",
    quote: "Working with POSF has been transformative for our company. The candidates they prepare are dedicated, skilled, and bring incredible resilience to our team.",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face"
  },
  {
    id: 3,
    name: "David Chen",
    role: "Program Graduate, 2023",
    quote: "The mentorship program connected me with professionals who believed in me when I didn't believe in myself. Today, I run my own small business.",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
  }
];

export const partners = [
  { 
    id: 1, 
    name: "Treatment Trends", 
    description: "Recovery and community healing services providing comprehensive case management support.",
    image: "https://customer-assets.emergentagent.com/job_posfoundation/artifacts/fug1euui_IMG_8639.heic",
    type: "Healthcare & Recovery",
    initials: "TT",
    color: "bg-teal-500"
  },
  { 
    id: 2, 
    name: "Lehigh County Adult Probation/Parole", 
    description: "Commonwealth of Pennsylvania Court of Common Pleas, providing vital reintegration support.",
    image: "https://customer-assets.emergentagent.com/job_posfoundation/artifacts/usm2qeam_IMG_8371.heic",
    type: "Government",
    initials: "LC",
    color: "bg-slate-700"
  },
  { 
    id: 3, 
    name: "Philly Truce", 
    description: "Violence reduction campaign achieving significant decreases in fatal shootings and homicides through community engagement.",
    image: "https://customer-assets.emergentagent.com/job_posfoundation/artifacts/cwxhvezm_IMG_0861.heic",
    type: "Community Safety",
    initials: "PT",
    color: "bg-amber-500"
  },
  { 
    id: 4, 
    name: "Pennsylvania CareerLink Lehigh Valley", 
    description: "Employment services and career development, with specialized programs for disabled veterans.",
    image: "https://customer-assets.emergentagent.com/job_posfoundation/artifacts/ygs3chvi_IMG_0726.heic",
    type: "Employment Services",
    initials: "CL",
    color: "bg-blue-600"
  },
  { 
    id: 5, 
    name: "Northbound & Co.", 
    description: "Strategic guidance and direction services supporting individuals on their journey to success.",
    image: "https://customer-assets.emergentagent.com/job_posfoundation/artifacts/qd71huwx_IMG_0725.heic",
    type: "Support Services",
    initials: "NB",
    color: "bg-emerald-600"
  }
];

export const programs = [
  {
    id: 1,
    title: "Educational Initiatives",
    description: "GED preparation, literacy programs, financial literacy workshops, and continuing education support to build a strong foundation for success.",
    icon: "GraduationCap",
    features: ["GED Preparation", "Financial Literacy", "Computer Skills", "Life Skills Training"]
  },
  {
    id: 2,
    title: "Workforce Development",
    description: "Job readiness training, resume building, interview preparation, and direct placement assistance with our partner companies.",
    icon: "Briefcase",
    features: ["Resume Building", "Interview Coaching", "Job Placement", "Career Counseling"]
  },
  {
    id: 3,
    title: "Corporate Partnerships",
    description: "We connect qualified candidates with forward-thinking employers committed to fair chance hiring practices.",
    icon: "Handshake",
    features: ["Employer Matching", "Workplace Integration", "Ongoing Support", "Retention Programs"]
  },
  {
    id: 4,
    title: "Mentorship & Support",
    description: "One-on-one mentoring with professionals, peer support groups, and comprehensive reintegration assistance.",
    icon: "Heart",
    features: ["1-on-1 Mentoring", "Peer Support Groups", "Housing Assistance", "Family Reunification"]
  }
];

export const aboutContent = {
  story: {
    title: "Our Story",
    paragraphs: [
      "The Productive Offenders of Society Foundation was established in 2023 with a simple but powerful belief: everyone deserves the opportunity to rebuild their life and contribute meaningfully to their community.",
      "Founded by JT Corra and a coalition of community leaders, social workers, and formerly incarcerated individuals who transformed their own lives, we understand the challenges of reintegration firsthand.",
      "What started as a small support group has grown into a comprehensive program serving hundreds of individuals annually, with partnerships spanning multiple industries and communities across Pennsylvania."
    ]
  },
  founder: {
    name: "JT Corra",
    title: "Founder",
    message: `2025 was a wild year with many ups and downs, cultural changes, even massive changes in our political landscape. I really am grateful for all the support and involvement in the community I had. Serving on various Reentry coalitions and committees was truly enjoyable and rewarding. Sitting on Lehigh County Drug and Alcohol Advisory board as a stakeholder giving advice on possible solutions to the county's drug problems from a lived experience perspective has been an honor.

This message was written from inside Lewisburg Federal Prison Camp and captures my journey over the past year — the wins, the losses, the resilience, and the continued commitment to justice reform, reentry, and healing even while incarcerated.

I have never stopped believing in this mission. Even in the hardest circumstances, I continue to serve, mentor, and build the future of this organization from the inside.

We stand for reentry. We stand for second chances.`,
    closing: "With respect and solidarity"
  },
  problem: {
    title: "The Challenge We Address",
    stats: [
      { number: "650,000+", label: "People released from prison annually in the U.S." },
      { number: "76%", label: "Rearrest rate within 5 years without proper support" },
      { number: "27%", label: "Unemployment rate among formerly incarcerated" }
    ],
    description: "Without proper support, education, and employment opportunities, the cycle of incarceration continues. We're here to break that cycle."
  },
  approach: {
    title: "How We Create Change",
    steps: [
      { title: "Assess", description: "We evaluate each participant's unique needs, skills, and goals." },
      { title: "Educate", description: "We provide tailored educational programs and skill development." },
      { title: "Connect", description: "We match participants with mentors and employment opportunities." },
      { title: "Support", description: "We provide ongoing assistance to ensure long-term success." }
    ]
  }
};

export const galleryImages = [
  { id: 1, src: "https://customer-assets.emergentagent.com/job_posfoundation/artifacts/4n34axh3_image000000.JPG", alt: "Recovery community gathering", caption: "Recovery Community Gathering" },
  { id: 2, src: "https://customer-assets.emergentagent.com/job_posfoundation/artifacts/t4bqheps_IMG_1669.jpeg", alt: "Team members", caption: "Community Outreach Team" },
  { id: 3, src: "https://customer-assets.emergentagent.com/job_posfoundation/artifacts/85llvf8v_IMG_3226.jpeg", alt: "Community event", caption: "Bridge Beyond Addiction Walk Event" }
];

export const volunteerOpportunities = [
  {
    id: 1,
    title: "Mentor",
    description: "Share your professional experience and provide guidance to program participants on their career journey.",
    commitment: "4-6 hours/month",
    icon: "Users"
  },
  {
    id: 2,
    title: "Workshop Facilitator",
    description: "Lead educational workshops in areas like financial literacy, computer skills, or professional development.",
    commitment: "2-4 hours/month",
    icon: "Presentation"
  },
  {
    id: 3,
    title: "Career Coach",
    description: "Help participants prepare resumes, practice interviews, and navigate the job search process.",
    commitment: "3-5 hours/month",
    icon: "Target"
  },
  {
    id: 4,
    title: "Event Volunteer",
    description: "Assist with community events, graduations, and partner networking sessions.",
    commitment: "As needed",
    icon: "Calendar"
  }
];

export const donationTiers = [
  { amount: 25, description: "Provides educational materials for one participant" },
  { amount: 50, description: "Funds a professional development workshop seat" },
  { amount: 100, description: "Supports one month of mentorship coordination" },
  { amount: 250, description: "Sponsors a participant's complete job readiness training" },
  { amount: 500, description: "Provides emergency support services for a family" },
  { amount: 1000, description: "Funds a full scholarship for our comprehensive program" }
];
